/**
 * Auth wire shapes — API Specification §5.1 plus `GET /auth/me`
 * (Phase 13A, ADR-13A-001). Mirrors `apps/api/src/auth/auth.controller.ts`
 * and `session-context.service.ts`.
 */

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
}

export interface MfaChallengeResponse {
  mfa_required: true;
  mfa_challenge_token: string;
}

/** `POST /auth/login` returns one or the other. */
export type LoginResponse = TokenResponse | MfaChallengeResponse;

export function isMfaChallenge(r: LoginResponse): r is MfaChallengeResponse {
  return (r as MfaChallengeResponse).mfa_required === true;
}

export interface SessionPermission {
  code: string;
  /** PermissionScopeType, minus 'none' (filtered out server-side). */
  scope_type: 'platform' | 'platform_readonly' | 'tenant' | 'tenant_readonly' | 'branch' | 'self';
}

/** `GET /auth/me` — the caller's own identity + effective permissions. */
export interface SessionContext {
  user_account_id: string;
  email: string;
  display_name: string;
  role_code: RoleCode;
  role_name: string;
  role_scope_class: 'platform' | 'platform_readonly' | 'tenant' | 'branch' | 'self';
  tenant_id: string | null;
  tenant_name: string | null;
  employee_id: string | null;
  employee_number: string | null;
  branch_ids: string[];
  /** The tenant's default IANA time zone; null for a platform account. */
  time_zone: string | null;
  /** IANA zone per branch id: the user's own branches when branch-scoped, else every branch of the tenant. */
  branch_time_zones: Record<string, string>;
  mfa_enrolled: boolean;
  permissions: SessionPermission[];
}

export type RoleCode = 'PSA' | 'PCA' | 'SUP' | 'TCA' | 'HRM' | 'TPA' | 'BMS' | 'EMP';
